import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for adding auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('activeParticipant');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Colección de métodos HTTP para interactuar con la sección de participantes en la API
export const participantApi = {
  // Listado administrativo; el backend exige que el correo esté en ADMIN_EMAILS.
  getAll: () => api.get('/admin/participants'),
  // Obtener el perfil de un participante específico usando su identificador único
  getById: (id) => api.get(`/participants/${id}`),
  // Registrar un nuevo participante en la base de datos
  create: (data) => api.post('/participants', data),
  // Iniciar sesión buscando al participante por correo electrónico mediante POST
  login: (email, password) => api.post('/participants/login', { email, password }),
  // Actualizar los datos de perfil de un participante existente
  update: (id, data) => api.put(`/participants/${id}`, data),
  // Eliminar el registro de un participante por su ID
  delete: (id) => api.delete(`/participants/${id}`),
  // Consultar todas las sesiones de tests completadas por un participante particular
  getTestSessions: (id) => api.get(`/participants/${id}/test-sessions`),
};

// Test API
export const testApi = {
  createSession: (data) => api.post('/tests/session', data),
  getSessionResults: (sessionId) => api.get(`/tests/session/${sessionId}`),
  getParticipantResults: (participantId) => api.get(`/tests/participant/${participantId}`),
  storeMaturityScores: (data) => api.post('/tests/maturity-scores', data),
};

// Dashboard API
export const dashboardApi = {
  getOverview: (participantId) => api.get(`/dashboard/overview/${participantId}`),
  getProgress: (participantId, params = {}) => {
    const queryParams = new URLSearchParams(params);
    return api.get(`/dashboard/progress/${participantId}?${queryParams}`);
  },
  compareSessions: (participantId, sessionId1, sessionId2) => 
    api.get(`/dashboard/compare/${participantId}/${sessionId1}/${sessionId2}`),
  getMaturityDistribution: (participantId) => 
    api.get(`/dashboard/maturity-distribution/${participantId}`),
  getInsights: (participantId) => api.get(`/dashboard/insights/${participantId}`),
};

// API de Inteligencia Artificial
export const insightsApi = {
  // Obtener análisis previo si existe
  getInsight: (participantId, contextType, sessionId = null) => 
    api.get(`/insights/${participantId}`, { params: { context_type: contextType, session_id: sessionId } }),
  // Generar nuevo análisis usando IA
  generateInsight: (participantId, contextType, sessionId = null) => 
    api.post(`/insights/generate/${participantId}`, { context_type: contextType, session_id: sessionId })
};

// Utility functions
export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem('authToken', token);
    api.defaults.headers.Authorization = `Bearer ${token}`;
  } else {
    localStorage.removeItem('authToken');
    delete api.defaults.headers.Authorization;
  }
};

export const getAuthToken = () => localStorage.getItem('authToken');

export const isAuthenticated = () => !!getAuthToken();

export default api;
